import React from 'react';
import { useSEO } from '../hooks/useSEO';
import ProductHero from '../components/ProductHero';
import Workflow from '../components/Workflow';

const SECTORS = [
  'IT & Digital', 'Pharmaceutical', 'Manufacturing', 'Energy', 'Utilities',
  'Property', 'Infrastructure', 'Education', 'Professional Services',
  'Construction', 'Healthcare', 'Other',
];

const V2VideraPM = () => {
  useSEO({
    title: 'Videra PM | Configurable project delivery platform',
    description: 'Choose your sector and create a structured project delivery environment around the way you work — configurable Videra for IT, pharma, manufacturing, energy and more.',
    path: '/videra/pm',
  });

  return (
    <div data-testid="v2-videra-pm">
      <ProductHero
        eyebrow="Videra PM"
        title="Your sector. Your projects. Your Videra."
        subtitle="Create a project delivery platform around the way you work."
        body={(
          <>
            <p>Generic project-management tools often start with a blank workspace and expect organisations to adapt. Videra PM takes a different approach.</p>
            <p>Choose your sector and create a structured delivery environment around the type of projects your organisation manages.</p>
          </>
        )}
        primary={{ label: 'Create My Videra', href: 'https://pm.videra.app' }}
        secondary={{ label: 'Talk to Keystone', to: '/contact' }}
        supportingLine="Available to try with full functionality."
        heroImage="/images/construction/cp-portfolio-pmo.jpg"
        heroImageAlt="Videra PM configurable portfolio environment"
        testid="videra-pm-hero"
      />

      <section className="py-16 md:py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">Sectors</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 max-w-3xl">Pick a sector. Get a delivery environment shaped around it.</h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {SECTORS.map((s) => (
              <div key={s} className="bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-800" data-testid={`pm-sector-${s.toLowerCase().replace(/\W+/g,'-')}`}>
                {s}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">How it works</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 mb-10">Six steps from sector choice to portfolio.</h2>
          <Workflow testid="pm-workflow" steps={['Choose your sector','Create your environment','Define delivery','Add projects','Manage delivery','See the portfolio']} />
        </div>
      </section>

      <section className="py-20 md:py-28 bg-slate-950 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight leading-tight max-w-3xl">
            Your organisation shouldn&rsquo;t have to fit the software.
          </h2>
          <div className="mt-8 text-base lg:text-lg text-slate-300 leading-relaxed space-y-4">
            <p>Create the environment around your projects, governance and delivery model.</p>
            <p>Where lifecycle is relevant to the selected industry, connect lifecycle requirements and project interventions within the same environment.</p>
          </div>
          <div className="mt-10">
            <a href="https://pm.videra.app" className="inline-flex items-center gap-2 bg-white text-slate-900 px-7 py-3.5 rounded-md text-sm font-medium hover:bg-slate-100 transition-colors" data-testid="pm-create-cta">
              Create My Videra →
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default V2VideraPM;
